import { motion } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import Globe from "./Globe";
import { MaskedLine, scrollToId, EASE } from "./Reveal";

export default function HeroSection() {
  return (
    <section id="home" data-testid="hero-section" className="relative min-h-screen overflow-hidden">
      <Globe className="absolute inset-0" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(3,5,8,0.55)_78%,#030508_100%)] pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-ink to-transparent pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 min-h-screen flex flex-col justify-end pb-28 md:pb-32 pt-32">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8, ease: EASE }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-gold" />
          </span>
          <span className="text-[11px] tracking-[0.4em] uppercase text-white/60" data-testid="hero-supporting-line">
            Ajemi Energy — Energy Procurement &amp; Supply
          </span>
        </motion.div>

        <h1 className="font-outfit font-extralight tracking-tighter leading-[0.95] text-5xl sm:text-7xl lg:text-8xl text-white max-w-5xl" data-testid="hero-headline">
          <MaskedLine delay={0.35}>Your Energy</MaskedLine>
          <MaskedLine delay={0.5}>
            <span className="text-gold">Procurement Partner.</span>
          </MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subtext"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.9, ease: EASE }}
          className="mt-7 max-w-xl text-base md:text-lg text-white/65 leading-relaxed"
        >
          Ajemi helps businesses source, procure and manage petroleum products through
          trusted supply relationships — taking the complexity out of energy procurement
          across Nigeria and beyond.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.9, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-market-check-btn"
            onClick={() => scrollToId("market-check")}
            className="group inline-flex items-center gap-3 bg-gold text-ink font-outfit font-medium rounded-full pl-7 pr-2.5 py-2.5 text-base hover:bg-goldbright hover:shadow-[0_0_44px_rgba(217,164,65,0.5)] transition-all duration-300"
          >
            Start a Market Check
            <span className="bg-ink/10 rounded-full p-2 group-hover:rotate-45 transition-transform duration-300">
              <ArrowUpRight className="w-4 h-4" />
            </span>
          </button>
          <button
            data-testid="hero-request-procurement-btn"
            onClick={() => scrollToId("quote")}
            className="inline-flex items-center gap-2 border border-white/20 text-white/85 font-outfit rounded-full px-7 py-3.5 text-base hover:border-gold/60 hover:text-gold transition-colors duration-300 backdrop-blur-sm bg-black/20"
          >
            Request Procurement
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="mt-14 flex items-center gap-4 text-[11px] tracking-[0.3em] uppercase text-white/40"
          data-testid="hero-route-legend"
        >
          <span>Source</span>
          <span className="h-px w-8 bg-gradient-to-r from-gold/70 to-transparent" />
          <span>Procure</span>
          <span className="h-px w-8 bg-gradient-to-r from-gold/70 to-transparent" />
          <span>Move</span>
          <span className="h-px w-8 bg-gradient-to-r from-gold/70 to-transparent" />
          <span>Deliver</span>
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        onClick={() => scrollToId("problem")}
        className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-white/35 hover:text-gold transition-colors"
        aria-label="Scroll down"
      >
        <span className="w-[26px] h-[42px] rounded-full border border-current flex justify-center pt-2">
          <span className="w-1 h-1.5 rounded-full bg-current animate-scroll-dot" />
        </span>
        <ChevronDown className="w-3.5 h-3.5" />
      </motion.button>
    </section>
  );
}
